import { Utils } from '../../../../../common/js/Utils';
import { CoverpathBaseAccountCenterController } from '../../../../js/controllers/CoverpathBaseAccountCenterController';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { Component, ViewChild, OnInit } from '@angular/core';
import { Controller } from 'apps/coverpath-client/public/js/Controller';
import { AccountCenterSection } from '../utilities/accountCenterSection.component';
import { AccountCenterTable } from '../account-center-table/account-center-table.component';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterTableRow } from '../../../classes/account-center/AccountCenterTableRow';
import { ActionType } from '../../../classes/account-center/ActionType';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { AccountCenterTableCell } from '../../../classes/account-center/AccountCenterTableCell';
import { ProductRulesController } from '../../../../../common/js/ProductRulesController';
import { PremiumDisplay } from '../../../interfaces/Type';
import { Product } from '../../../../../common/js/static/Product';
import { CommonUtils } from '../../../../../common/js/static/CommonUtils';
import { PolicyData } from '../../../interfaces/PolicyData';
import type { MMDirectPolicy } from '../../../../classes/mmdirect_subclasses/MMDirectPolicy';
import type { CoverpathPolicy } from '../../../../classes/coverpath_subclasses/CoverpathPolicy';
import {
  Exchange1035FundingStatus,
  Exchange1035FundingPurpose,
} from '../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { Subscription } from 'rxjs';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

const dividendOptionMap = {
  PUA: 'Paid-up Additions',
  Cash: 'Cash',
  PRPUA: 'Reduce Premium/Paid-up Additions',
  PRCash: 'Reduce Premium/Cash',
  Accumulate: 'Dividend Accumulations',
};

// Status
const statusRowFunctions = {
  show: function () {
    return true;
  },
  header: function () {
    return new AccountCenterTableCell('heading', 'Status');
  },
  data: function (policyData: PolicyData, isCanceled) {
    const policyViewData = policyData.getCACPaymentData();
    let status;
    if (isCanceled) {
      status = 'Canceled';
    } else if (this.hasInForcePolicy) {
      status = `In Force: ${Utils.formatDate(policyViewData.getCACEffectiveDate())}`;
    } else {
      status = 'Pending';
    }
    const row = new AccountCenterTableCell('data-status', status);
    row.setCanceled(isCanceled);
    return row;
  },
};

const tableCreateDef = [
  // Effective Date
  {
    show: function (policyData: PolicyData) {
      // show only if the policy has an effective date and it's not in the future
      const effectiveDate = policyData.getCACPaymentData().getCACEffectiveDate();
      return !!effectiveDate && !Utils.isDateInTheFuture(effectiveDate);
    },
    header: function (policyData: PolicyData) {
      if (policyData.getCACIsInForce()) {
        return new AccountCenterTableCell('heading', 'Effective Date', null);
      } else {
        return new AccountCenterTableCell(
          'heading',
          'Effective Date',
          null,
          "Your financial professional set your policy's effective date in the past to reduce your ongoing premium."
        );
      }
    },
    data: function (policyData: PolicyData) {
      const effectiveDate = policyData.getCACPaymentData().getCACEffectiveDate();
      return new AccountCenterTableCell(
        'data',
        !!effectiveDate ? `${Utils.formatDate(effectiveDate)}` : 'Same as Issue Date'
      );
    },
  },
  // Death Benefit
  {
    show: function () {
      return true;
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Death Benefit', null);
    },
    data: function (policyData: PolicyData) {
      return new AccountCenterTableCell(
        'data',
        `${Utils.formatIntCurrency(policyData.getCACPaymentData().getCACFaceAmount().toString())}`
      );
    },
  },
  //Cash Surrender Value
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACIsWholeLife() && CoverpathUtils.isPostIssue(policyData.getCACWorkflowState());
    },
    header: function () {
      return new AccountCenterTableCell(
        'heading',
        'Cash Surrender Value',
        null, // action item
        'The amount of money you will receive if you cancel your whole life policy today. Taxes may be owed on this amount. '
      );
    },
    data: async function (policyData: PolicyData, policy: MMDirectPolicy | CoverpathPolicy) {
      if (!policyData.getCACIsWholeLife() || !CoverpathUtils.isPostIssue(policyData.getCACWorkflowState())) {
        return PolicyDetailsTableComponent.getNaCell();
      }
      try {
        const isCanceledInPA = await this.accountCenterController.isCanceledInPA(policy.policyNumber);
        if (isCanceledInPA) {
          return PolicyDetailsTableComponent.getNaCell();
        }
        const cashValueQuote = await this.accountCenterService.getCashValue(policy);
        return new AccountCenterTableCell('data', null, {
          displayText: 'Cash Surrender Value',
          actionType: ActionType.RenderComponent,
          component: 'cash-value',
          property: cashValueQuote,
        });
      } catch (e) {
        return PolicyDetailsTableComponent.getNaCell();
      }
    },
  },
  //Convertible Until
  {
    show: function (policyData: PolicyData) {
      if (policyData.getCACIsLifeBridge()) {
        return false;
      } else {
        return (
          (policyData.getCACIsTermLifePolicy() || policyData.getCACIsJointApp()) && policyData.getCACHasAnyInForce()
        );
      }
    },
    header: function (policyData: PolicyData) {
      const isEcpProductWithPendingAmendment =
        policyData.getCACIsEcpProduct() || (policyData && policyData.getCACIsEcpProduct());
      let toolTipText =
        'Until this date, you can convert some ' + 'or all of your Term coverage into Whole Life coverage.';
      if (isEcpProductWithPendingAmendment) {
        toolTipText +=
          '<br><br>Your policy has an Extended Conversion' + ' Period (ECP), which allows more time to convert.';
      }

      return new AccountCenterTableCell('heading', 'Convertible Until', null, toolTipText);
    },
    data: function (policyData: PolicyData, policy: MMDirectPolicy | CoverpathPolicy) {
      if (policyData.getCACIsWholeLife()) {
        return PolicyDetailsTableComponent.getNaCell();
      }

      const conversionDate = policy.getConvertibilityUntilDate(this.productRulesController.maximumConvertibleAge);
      const dataText = conversionDate ? conversionDate.toLocaleDateString('en-US') : 'N/A';
      const convertibleDate =
        policyData.getCACPaymentData() && policyData.getCACPaymentData().getCACConversionEndDate()
          ? new Date(policyData.getCACPaymentData().getCACConversionEndDate()).toLocaleDateString('en-US')
          : dataText;

      return new AccountCenterTableCell('data', convertibleDate);
    },
  },
  //Expires
  {
    show: function (policyData: PolicyData) {
      return (policyData.getCACIsTermLifePolicy() || policyData.getCACIsJointApp()) && policyData.getCACHasAnyInForce();
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Expires');
    },
    data: function (policyData: PolicyData) {
      return PolicyDetailsTableComponent.constructExpiryCell(policyData);
    },
  },
  //Waiver of Premium
  {
    show: function () {
      return true;
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Waiver of Premium');
    },
    data: function (policyData: PolicyData) {
      return new AccountCenterTableCell('data', policyData.getCACHasWaiver() ? 'Yes' : 'No');
    },
  },
  // 1035 Exchange
  {
    show: function (policyData: PolicyData) {
      const primaryPolicyViewData = policyData.getCACPaymentData();
      const cacAlirRider = primaryPolicyViewData.getCACAlirRider();
      const cacHasAlir = primaryPolicyViewData.getCACHasAlirRider();
      const has1035Exchange = cacHasAlir && cacAlirRider.lumpSumUses1035;
      return cacHasAlir && has1035Exchange;
    },
    header: function () {
      const toolTipText =
        'A 1035 Exchange is a transfer of cash value from an existing life insurance policy into this new policy.';
      return new AccountCenterTableCell('heading', '1035 Exchange', null, toolTipText);
    },
    data: async function (policyData: PolicyData, policy: MMDirectPolicy | CoverpathPolicy) {
      if (policyData.getCACIsSecondary()) {
        return PolicyDetailsTableComponent.getNaCell();
      }

      let dataText: string;
      if (policyData.getCACIsCoverpathPolicy()) {
        const exchangeData = await this.accountCenterService.get1035ExchangeData(policy);
        if (exchangeData) {
          dataText = `${Utils.formatCurrency(exchangeData.amount, 0)}`;
          if (exchangeData.status !== Exchange1035FundingStatus.COMPLETED) {
            dataText += ' (estimated)';
          }
        } else {
          return new AccountCenterTableCell('data', 'Not available. Please try again later');
        }
      }

      const cacPaymentData = policyData.getCACPaymentData();
      const cacExchange1035 = cacPaymentData.getCACExchange1035();
      if (
        cacExchange1035.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR &&
        !this.isPremiumPending(policyData)
      ) {
        return new AccountCenterTableCell('data', null, {
          displayText: dataText,
          actionType: ActionType.RenderComponent,
          component: 'exchange-1035-breakdown',
        });
      } else {
        return new AccountCenterTableCell('data', dataText);
      }
    },
  },
  // ALIR Scheduled/Unscheduled
  {
    show: function (policyData: PolicyData) {
      const primaryPolicyPaymentViewData = policyData.getCACPaymentData();
      const cacAlirRider = primaryPolicyPaymentViewData.getCACAlirRider();
      const hasCACAlirRider = primaryPolicyPaymentViewData.getCACHasAlirRider();
      const has1035Exchange = cacAlirRider && cacAlirRider.lumpSumUses1035;
      const hasScheduledPayments = cacAlirRider && cacAlirRider.hasScheduledPayments;
      const cacExchange1035 = primaryPolicyPaymentViewData.getCACExchange1035();
      if (
        has1035Exchange &&
        cacExchange1035.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR &&
        this.isPremiumPending(policyData)
      ) {
        return false; // Don't show ALIR row for 1035 to Premium when premium is pending
      }

      return hasCACAlirRider && (hasScheduledPayments || cacExchange1035.enabled);
    },
    header: function () {
      const toolTipText =
        'This rider allows you to purchase additional life insurance with one-time payments or transfers from other policies';
      return new AccountCenterTableCell('heading', 'Additional Life Insurance Rider', null, toolTipText);
    },
    data: async function (policyData: PolicyData, policy: MMDirectPolicy | CoverpathPolicy) {
      if (policyData.getCACIsSecondary()) {
        return PolicyDetailsTableComponent.getNaCell();
      }

      const cacPaymentData = policyData.getCACPaymentData();
      const cacAlirRider = cacPaymentData.getCACAlirRider();
      const cacExchange1035 = cacPaymentData.getCACExchange1035();

      if (cacAlirRider && cacAlirRider.hasScheduledPayments) {
        // Scheduled payments only -- no 1035 Exchange
        const isAnnual: boolean =
          CommonUtils.makePaymentFrequencySafeForPolicy(cacAlirRider.paymentFrequency) === 'annual';
        const formattedAmount = Utils.formatCurrency(cacAlirRider.premium);
        const paymentSchedule = isAnnual ? `${formattedAmount}/year` : `${formattedAmount}/month`;
        return new AccountCenterTableCell('data', paymentSchedule);
      } else {
        // Policy has 1035 exchange
        let dataText: string;
        if (policyData.getCACIsCoverpathPolicy()) {
          const exchangeData = await this.accountCenterService.get1035ExchangeData(policy);
          if (exchangeData) {
            let alirAmount = exchangeData.amount;
            if (cacExchange1035.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
              const totalPremium = cacPaymentData.getCACTotalPremium();
              alirAmount = Math.max(exchangeData.amount - totalPremium, 0);
            }
            dataText = `${Utils.formatCurrency(alirAmount, 0)}`;
            if (exchangeData.status !== Exchange1035FundingStatus.COMPLETED) {
              dataText += ' (estimated)';
            }
          } else {
            dataText = 'Not available. Please try again later';
          }
        }
        return new AccountCenterTableCell('data', dataText);
      }
    },
  },
  // GIR Rider
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACPaymentData().getCACHasGirRider();
    },
    header: function () {
      const toolTipText =
        'You have the option to purchase ' +
        'additional coverage (at a ' +
        'guaranteed price) at specific ' +
        'times in the future.';
      return new AccountCenterTableCell('heading', 'Guaranteed Insurability Option', null, toolTipText);
    },
    data: function (policyData: PolicyData) {
      if (policyData.getCACIsSecondary()) {
        return PolicyDetailsTableComponent.getNaCell();
      }
      const riderAmount = policyData.getCACPaymentData().getCACgirRider().amount;
      return new AccountCenterTableCell(
        'data',
        riderAmount ? Utils.formatIntCurrency(riderAmount.toString()) : 'No value entered'
      );
    },
  },
  // RTR Rider
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACPaymentData().getCACHasRtrRider();
    },
    header: function () {
      const toolTipText =
        'This adds term coverage to your ' + 'whole life policy. This price of this ' + 'rider increases annually.';
      return new AccountCenterTableCell('heading', 'Renewable Term Rider', null, toolTipText);
    },
    data: function (policyData: PolicyData) {
      if (policyData.getCACIsSecondary()) {
        return PolicyDetailsTableComponent.getNaCell();
      }
      const riderAmount = policyData.getCACPaymentData().getCACrtrRider().amount;
      return new AccountCenterTableCell(
        'data',
        riderAmount ? Utils.formatIntCurrency(riderAmount.toString()) : 'No value entered'
      );
    },
  },
  // CIR Rider
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACPaymentData().getCACHasCirRider();
    },
    header: function () {
      const toolTipText =
        'This rider allows you to access part of the policy’s death benefit if the insured becomes chronically ill.';
      return new AccountCenterTableCell('heading', 'Chronic Illness Rider', null, toolTipText);
    },
    data: function (policyData: PolicyData) {
      if (policyData.getCACIsSecondary()) {
        return PolicyDetailsTableComponent.getNaCell();
      }
      const riderAmount = policyData.getCACPaymentData().getCACCirRider().amount;
      return new AccountCenterTableCell('data', riderAmount ? Utils.formatIntCurrency(riderAmount.toString()) : 'N/A');
    },
  },
  // ADBR
  {
    show: function () {
      return true;
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Accelerated Death Benefit');
    },
    data: function (policyData: PolicyData) {
      return new AccountCenterTableCell('data', policyData.getCACIncludeAdbr() ? 'Yes' : 'No');
    },
  },
  // Transfer of Insured
  {
    show: function (policyData: PolicyData) {
      return (
        policyData.getCACProduct() === Product.channels.MMTRAD.productCodes.MMTRADWL100 ||
        policyData.getCACProduct() === Product.channels.MMTRAD.productCodes.MMTRADWL65 ||
        policyData.getCACProduct() === Product.channels.MMTRAD.productCodes.MMTRADWL20
      );
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Transfer Of Insured');
    },
    data: function () {
      return new AccountCenterTableCell('data', 'Yes');
    },
  },
  // Dividend Options
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACIsWholeLife() || policyData.getCACIsJointApp();
    },
    header: function () {
      return new AccountCenterTableCell(
        'heading',
        'Dividend Option',
        null, // action item
        `<p class='tooltip__section'>MassMutual may pay a non-guaranteed dividend to its Whole Life policy holders. There are different ways to receive this dividend. Here are some of the options: </p>
                   <p class='tooltip__section'><span class='tooltip__header'>Cash: </span>The non-guaranteed dividend could be paid via ACH.</p>
                   <p class='tooltip__section'><span class='tooltip__header'>Premium Reduction: </span>The non-guaranteed dividend may be used to pay your premium. If the dividend is insufficient to pay the entire premium, MassMutual will bill for the remainder due. Excess dividends are applied to the secondary dividend option. MassMutual offers two secondary dividend options: ‘paid up additions’ or ‘cash’.</p>
                   <p class='tooltip__section'><span class='tooltip__header'>Paid-Up Additions: </span>Used to purchase participating paid up additional insurance. Paid-up additions may increase your death benefit and cash value. This has a compounding effect on the non-guaranteed dividends you may receive in the future.</p>
                   <p class='tooltip__section'><span class='tooltip__header'>Accumulation: </span>Non-guaranteed dividends are held by MassMutual and accumulate with interest. The interest rate is updated by MassMutual every year.</p>`,
        'tooltip-wide'
      );
    },
    data: function (policyData: PolicyData) {
      if (!policyData.getCACIsWholeLife()) {
        return PolicyDetailsTableComponent.getNaCell();
      }
      return new AccountCenterTableCell('data', dividendOptionMap[policyData.getCACDividendOption()]);
    },
  },
  //Loan Rate
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACIsWholeLife() || policyData.getCACIsJointApp();
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Loan Rate');
    },
    data: function (policyData: PolicyData) {
      if (!policyData.getCACIsWholeLife()) {
        return PolicyDetailsTableComponent.getNaCell();
      }
      return new AccountCenterTableCell('data', policyData.getCACLoanInterestType());
    },
  },
  //APL
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACIsWholeLife() || policyData.getCACIsJointApp();
    },
    header: function () {
      return new AccountCenterTableCell(
        'heading',
        'Automatic Premium Loan',
        null, // action item
        'If you choose to have the Automatic Premium Loan (APL) option on your policy, your policy will automatically take out a loan against the policy’s cash value if you have not paid premiums. If the cash value is not sufficient to pay an annual premium, APL will automatically pay a monthly premium instead.'
      );
    },
    data: function (policyData: PolicyData) {
      if (!policyData.getCACIsWholeLife()) {
        return PolicyDetailsTableComponent.getNaCell();
      }
      return new AccountCenterTableCell('data', policyData.getCACIsWholeAPL() ? 'Yes' : 'No');
    },
  },
  // Coverage Term
  {
    show: function () {
      return true;
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Coverage Term');
    },
    data: function (policyData: PolicyData) {
      return new AccountCenterTableCell(
        'data',
        policyData.getCACIsWholeLife() ? 'Whole Life' : `${policyData.getCACTerm()} Years`
      );
    },
  },
  // Policy Number
  {
    show: function () {
      return true;
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Policy Number');
    },
    data: function (policyData: PolicyData, policy: MMDirectPolicy | CoverpathPolicy) {
      return new AccountCenterTableCell('data', policy.policyNumber);
    },
  },
];

@Component({
  selector: 'policy-details-table',
  moduleId: module.id,
  template: '<account-center-table></account-center-table>',
})
export class PolicyDetailsTableComponent extends AccountCenterSection implements OnInit {
  @ViewChild(AccountCenterTable, { static: true }) table: AccountCenterTable;
  static title: string = 'Policy Details';
  controller: Controller;
  accountCenterController: CoverpathBaseAccountCenterController;
  productRulesController: ProductRulesController;
  primaryPolicyNumber: string;
  primaryPolicy: MMDirectPolicy | CoverpathPolicy;
  primaryPolicyData: PolicyData;
  secondaryPolicyNumber: string;
  secondaryPolicy: MMDirectPolicy | CoverpathPolicy;
  secondaryPolicyData: PolicyData;
  postIssueDataSusbcription: Subscription;

  constructor(amorphicService: AmorphicService, accountCenterService: AccountCenterService) {
    super(amorphicService, accountCenterService);
    this.controller = amorphicService.controller as Controller;
    this.accountCenterController = this.controller.accountCenterController;
    this.productRulesController = this.controller.productRulesController;
  }

  async ngOnInit(): Promise<void> {
    await this.resetData();
    this.subscribeToAccountCenterService();
  }

  ngOnDestroy() {
    if (this.postIssueDataSusbcription) {
      this.postIssueDataSusbcription.unsubscribe();
    }
  }

  async processWorkflowAction(workflowAction: string): Promise<void> {
    await this.resetData();
  }

  async resetData() {
    if (this.accountCenterService) {
      this.primaryPolicy = this.accountCenterService.application.primaryPolicyWithPendingAmendment;
      this.primaryPolicyNumber = this.primaryPolicy.policyNumber;
      this.primaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(this.primaryPolicy.__id__ as string);

      if (this.primaryPolicyData.getCACIsJointApp()) {
        this.secondaryPolicy = this.accountCenterService.application.secondaryPolicyWithPendingAmendment;
        this.secondaryPolicyNumber = this.secondaryPolicy.policyNumber;
        this.secondaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(
          this.secondaryPolicy.__id__ as string
        );
      }
    }

    if (this.hasInForcePolicy || this.hasCanceledPolicy) {
      this.table.title = 'Policy Details';
    } else {
      this.table.title = 'Application Details';
    }
    await this.constructTableForPolicies();
  }

  static getNaCell(): AccountCenterTableCell {
    return new AccountCenterTableCell('data', 'N/A');
  }

  private async constructTable() {
    const rows: Array<AccountCenterTableRow> = [];

    if (this.primaryPolicyData.getCACIsJointApp()) {
      try {
        let isPrimaryCanceled = false;
        let isSecondaryCanceled = false;
        if (CoverpathUtils.isPostIssue(this.workflowAction)) {
          isPrimaryCanceled = await this.accountCenterController.isCanceledInPA(this.primaryPolicyNumber);
          isSecondaryCanceled = await this.accountCenterController.isCanceledInPA(this.secondaryPolicyNumber);
        }

        const secondaryPolicyTermLength = this.secondaryPolicyData.getCACTerm();
        const secondaryTermLengthDescription =
          secondaryPolicyTermLength === 1
            ? Product.products.CASTerm.artDescription
            : `${secondaryPolicyTermLength} Year Term`;

        rows.push(
          new AccountCenterTableRow(
            [
              new AccountCenterTableCell('heading', ''),
              new AccountCenterTableCell('data-heading', `Whole Life ${this.primaryPolicyData.getCACTerm()}`),
              new AccountCenterTableCell('data-heading', secondaryTermLengthDescription),
            ],
            'joint-header'
          )
        );

        const headerStatus = statusRowFunctions.header.call(this, this.primaryPolicyData);

        const wholeStatus = statusRowFunctions.data.call(this, this.primaryPolicyData, isPrimaryCanceled);

        const termStatus = statusRowFunctions.data.call(this, this.secondaryPolicyData, isSecondaryCanceled);
        termStatus.setPolicyLabel(`Term ${this.secondaryPolicyData.getCACTerm()}`);
        const promises = [];
        rows.push(new AccountCenterTableRow([headerStatus, wholeStatus, termStatus]));
        tableCreateDef.forEach((def) => {
          promises.push(
            this.createRowFromDef(
              def,
              this.primaryPolicy,
              this.secondaryPolicy,
              this.primaryPolicyData,
              this.secondaryPolicyData,
              isPrimaryCanceled,
              isSecondaryCanceled
            )
          );
        });
        const temp = await Promise.all(promises);
        const tableRows = temp.filter((row) => row !== undefined);
        rows.push(...tableRows);
      } catch (err) {
        this.table.hideTable = false;
        const error = {
          isHumanRelated: false,
          name: err.name,
          message: err.message,
          stack: err.stack,
          data: {
            primaryPolicyNumber: this.primaryPolicyNumber,
            applicationNumber: this.accountCenterService.application.applicationNumber,
          },
        };
        UILoggerSingleton.getInstance().error({
          module: 'policy-details-table.component',
          function: 'constructTable',
          category: UICategory.request,
          error: error,
        });
      }
    } else {
      rows.push(
        new AccountCenterTableRow([
          statusRowFunctions.header.call(this, this.primaryPolicyData),
          statusRowFunctions.data.call(this, this.primaryPolicyData, false),
        ])
      );
      const promises = [];
      tableCreateDef.forEach((def) => {
        promises.push(this.createRow(def));
      });
      const tableRows = (await Promise.all(promises)).filter((row) => row !== undefined);
      rows.push(...tableRows);
    }
    return rows;
  }

  private async createRow(def): Promise<AccountCenterTableRow> {
    if (def.show.call(this, this.primaryPolicyData, this.primaryPolicy)) {
      return new AccountCenterTableRow([
        def.header.call(this, this.primaryPolicyData),
        await def.data.call(this, this.primaryPolicyData, this.primaryPolicy),
      ]);
    }
  }

  private async createRowFromDef(
    def,
    primaryPolicy: MMDirectPolicy | CoverpathPolicy,
    secondaryPolicy: MMDirectPolicy | CoverpathPolicy,
    primaryPolicyData: PolicyData,
    secondaryPolicyData: PolicyData,
    isPrimaryCanceled: boolean,
    isSecondaryCanceled: boolean
  ): Promise<AccountCenterTableRow> {
    if (
      def.show.call(this, primaryPolicyData, primaryPolicy) ||
      def.show.call(this, secondaryPolicyData, secondaryPolicy)
    ) {
      const header = def.header.call(this, primaryPolicyData);
      const wholeLifeCell = await def.data.call(this, primaryPolicyData, primaryPolicy);
      wholeLifeCell.setCanceled(isPrimaryCanceled);

      const termCell = await def.data.call(this, secondaryPolicyData, secondaryPolicy);
      termCell.setPolicyLabel(`Term ${secondaryPolicyData.getCACTerm()}`);
      termCell.setCanceled(isSecondaryCanceled);

      return new AccountCenterTableRow([header, wholeLifeCell, termCell]);
    }
  }

  private async constructTableForPolicies(): Promise<void> {
    this.table.isJointApp = this.primaryPolicyData.getCACIsJointApp();
    this.table.rows = await this.constructTable();
  }

  public static constructExpiryCell(primaryPolicyData: PolicyData): AccountCenterTableCell {
    let dataText;
    const expirationDate = primaryPolicyData.getCACPaymentData().getCACPolicyEndDate();
    if (primaryPolicyData.getCACIsWholeLife()) {
      dataText = 'Never';
    } else if (!expirationDate) {
      dataText = 'Pending';
    } else {
      const date = new Date(expirationDate);
      dataText = date.toLocaleDateString();
    }

    return new AccountCenterTableCell('data', dataText);
  }

  private isPremiumPending(policyData: PolicyData) {
    const premiumDisplay: PremiumDisplay = policyData
      .getCACPaymentData()
      .getCACPremiumDisplayObj(this.workflowAction, policyData, false);
    return premiumDisplay.premiumText && premiumDisplay.premiumText.toLowerCase().includes('pending');
  }
}
